/**
 * Mark Deal Lost Handler
 * Edit handler for marking deals as lost
 */

import type { ToolExecutionContext } from '../../types';
import type { EditPreview } from '../../types';
import { EditHandlerBase } from '../base';
import { uuidField } from '../../utils/schema-builder';
import { generateDiff } from '../../utils/diff-generator';
import { generateConfirmationToken } from '../../utils/confirmation-token';
import { createUserGraphQLClient } from '../../utils/graphql-client';
import { validateDealId } from '../../utils/deal-validation';

/**
 * Mark deal lost handler
 * Marks a deal as lost and sets the lost_at timestamp
 */
export class MarkDealLostHandler extends EditHandlerBase<any> {
  id = 'mark_deal_lost';

  schema = {
    type: 'object',
    properties: {
      id: uuidField('UUID of the deal to mark as lost'),
      reason: {
        type: 'string',
        description: 'Reason for losing the deal (optional)',
      },
      notes: {
        type: 'string',
        description: 'Optional notes about the loss',
      },
    },
    required: ['id'],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<EditPreview> {
    // Validate input
    const id = validateDealId(params.id);
    const reason = params.reason;
    const notes = params.notes;

    if (!context?.userToken) {
      throw new Error('User token is required for deal update');
    }

    try {
      const { GetDealDocument } = await import('@/src/gql/graphql');
      
      // Create user-scoped GraphQL client that respects RLS
      const client = createUserGraphQLClient(context.userToken);

      // Fetch current deal
      const result = await client.query({
        query: GetDealDocument,
        variables: {
          id,
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const currentDeal = data?.dealsCollection?.edges?.[0]?.node;
      
      if (!currentDeal) {
        throw new Error(`Deal with ID "${id}" not found or you don't have permission to edit it`);
      }

      // Check if deal is already won or lost
      if (currentDeal.stage === 'won' || currentDeal.stage === 'lost') {
        throw new Error(`Cannot mark deal as lost: deal is already in "${currentDeal.stage}" stage`);
      }

      // Build updates
      const updates: any = {
        stage: 'lost',
        lost_at: new Date().toISOString(),
      };

      // Build loss notes
      let lossNotes = '[Lost]';
      if (reason) {
        lossNotes += ` Reason: ${reason}`;
      }
      if (notes) {
        lossNotes += ` - ${notes}`;
      }

      updates.notes = (currentDeal.notes || '') + (currentDeal.notes ? '\n\n' : '') + lossNotes;

      // Generate merged state
      const merged = { ...currentDeal, ...updates };
      const changes = generateDiff(currentDeal, merged);

      // Generate confirmation token
      const token = generateConfirmationToken({
        entityType: 'deal',
        entityId: id,
        changes: updates,
        userId: context?.userId || 'system',
        chatId: context?.chatId || 'system',
        messageId: context?.messageId || 'system',
      });

      return {
        type: 'preview',
        entity: 'deal',
        id,
        changes,
        confirmationToken: token,
        preview: merged,
      };
    } catch (error) {
      throw new Error(`Mark deal lost failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
